import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip
} from '@mui/material';
import {
  Security as SecurityIcon,
  LockOutlined as LockIcon,
  VpnKey as KeyIcon,
  Policy as PolicyIcon,
  Https as HttpsIcon,
  Shield as ShieldIcon,
  BugReport as BugIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import DocumentTypeTemplate from '../DocumentTypeTemplate';

const SecurityGuidelines = ({ projectId, existingContent = '', onSave }) => {
  const renderSecurityPreview = (content) => {
    // Render as markdown by default
    return (
      <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1 }}>
        <ReactMarkdown>{content}</ReactMarkdown>
      </Box>
    );
  };
  
  // Security-specific prompt configuration
  const securityPromptConfig = {
    system: "You are a security specialist with expertise in application security, compliance, and best practices.",
    examples: [
      "What authentication methods should we implement for our healthcare application?",
      "What are the key security considerations for a financial application?"
    ],
    instruction: "Provide detailed security guidelines and recommendations based on industry best practices."
  };
  
  return (
    <DocumentTypeTemplate
      projectId={projectId}
      documentType="securityGuidelines"
      title="Security Guidelines"
      icon={SecurityIcon}
      existingContent={existingContent}
      onSave={onSave}
      renderPreview={renderSecurityPreview}
      promptConfig={securityPromptConfig}
      allowedImportTypes="text/plain,text/markdown,.md"
    >
      {/* Additional security-specific content */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Security Checklist</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <List>
          <ListItem>
            <ListItemIcon>
              <LockIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Authentication" 
              secondary="Implement secure authentication mechanisms (OAuth 2.0, JWT, MFA)" 
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <PolicyIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Authorization" 
              secondary="Implement proper role-based access control (RBAC)" 
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <HttpsIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Data Protection" 
              secondary="Encrypt sensitive data at rest and in transit" 
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <KeyIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="API Security" 
              secondary="Secure APIs with proper authentication, rate limiting, and input validation" 
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <BugIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Vulnerability Management" 
              secondary="Regular security testing, dependency scanning, and patching" 
            />
          </ListItem>
          
          <ListItem>
            <ListItemIcon>
              <VisibilityIcon color="primary" />
            </ListItemIcon>
            <ListItemText 
              primary="Logging & Monitoring" 
              secondary="Implement comprehensive logging and monitoring for security events" 
            />
          </ListItem>
        </List>
      </Paper>
      
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Compliance Standards</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <ShieldIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="subtitle1" gutterBottom>GDPR</Typography>
              <Typography variant="body2" color="text.secondary">
                European data protection regulations
              </Typography>
              <Chip 
                label="Data Privacy" 
                size="small" 
                color="primary" 
                variant="outlined" 
                sx={{ mt: 1 }} 
              />
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <ShieldIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="subtitle1" gutterBottom>HIPAA</Typography>
              <Typography variant="body2" color="text.secondary">
                Healthcare data protection standards
              </Typography>
              <Chip 
                label="Healthcare" 
                size="small" 
                color="primary" 
                variant="outlined" 
                sx={{ mt: 1 }} 
              />
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
              <ShieldIcon color="primary" sx={{ fontSize: 40 }} />
              <Typography variant="subtitle1" gutterBottom>PCI DSS</Typography>
              <Typography variant="body2" color="text.secondary">
                Payment card industry standards
              </Typography>
              <Chip 
                label="Payments" 
                size="small" 
                color="primary" 
                variant="outlined" 
                sx={{ mt: 1 }} 
              />
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </DocumentTypeTemplate>
  );
};

export default SecurityGuidelines;